/**
 * My information -> Preferences: what Kono sends this buyer ("Your materials"
 * alerts and how often, the quarterly summary, early access to deals) and the
 * saved specs profile (certs, standards, tolerances, preferred ship-from
 * states, maximum lead time) that pre-filters their searches.
 *
 * Each block only shows when the matching switch is on internally
 * (Configurations -> Sales and UI -> Buyer care).
 */
import { useEffect, useState } from "react";
import {
  BuyerPreferences as Prefs,
  BuyerPrefs,
  ComplianceKey,
  DigestFrequency,
  loadBuyerPreferences,
  saveBuyerPreferences,
} from "../buyerCare";

const COMPLIANCE: { key: ComplianceKey; label: string }[] = [
  { key: "dfarsCompliant", label: "DFARS compliant" },
  { key: "domesticMelt", label: "Domestic melt (USA)" },
  { key: "singleHeat", label: "Single heat" },
  { key: "mtrAvailable", label: "MTR / certs available" },
];

const FREQUENCIES: { value: DigestFrequency; label: string }[] = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every two weeks" },
];

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

const input =
  "rounded border border-gray-300 px-2 py-1 text-sm focus:border-[#00062e] focus:outline-none";

const Switch = ({
  on,
  onChange,
  label,
  help,
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  label: string;
  help?: string;
}) => (
  <label className="flex items-start gap-2 text-sm text-gray-800">
    <input
      type="checkbox"
      className="mt-0.5"
      checked={on}
      onChange={(e) => onChange(e.target.checked)}
    />
    <span>
      <span className="font-medium">{label}</span>
      {help && <span className="block text-xs text-gray-600">{help}</span>}
    </span>
  </label>
);

export const BuyerPreferences = ({ uid }: { uid: string }) => {
  const [data, setData] = useState<Prefs | null>(null);
  const [draft, setDraft] = useState<BuyerPrefs | null>(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [standardsText, setStandardsText] = useState("");

  useEffect(() => {
    let alive = true;
    loadBuyerPreferences(uid).then((p) => {
      if (!alive || !p) return;
      setData(p);
      setDraft(p.prefs);
      setStandardsText(p.prefs.specs.standards.join(", "));
    });
    return () => {
      alive = false;
    };
  }, [uid]);

  if (!data || !draft) return null;
  const { available, effective, defaults } = data;
  if (!available.alerts && !available.quarterly && !available.deals && !available.specs) return null;

  const set = (patch: Partial<BuyerPrefs>) => {
    setDraft({ ...draft, ...patch });
    setSaved(false);
  };
  const setSpecs = (patch: Partial<BuyerPrefs["specs"]>) => set({ specs: { ...draft.specs, ...patch } });
  const alertsOn = draft.alerts.enabled ?? effective.alerts.enabled;
  const frequency = draft.alerts.frequency ?? effective.alerts.frequency;

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const standards = standardsText
        .split(/[,;\n]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const next = await saveBuyerPreferences(uid, { ...draft, specs: { ...draft.specs, standards } });
      if (next) {
        setData(next);
        setDraft(next.prefs);
        setStandardsText(next.prefs.specs.standards.join(", "));
      }
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "We could not save your preferences.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div id="preferences" className="mt-10 border-t border-gray-200 pt-6">
      <h2 className="text-lg font-semibold">Preferences</h2>
      <p className="mt-1 text-sm text-gray-600">
        What we send you and what your searches start with. Nothing here is shared with vendors.
      </p>

      {(available.alerts || available.quarterly || available.deals) && (
        <div className="mt-4 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Emails from Kono</p>
          {available.alerts && (
            <div className="rounded border border-gray-200 bg-white p-3">
              <Switch
                on={alertsOn}
                onChange={(v) => set({ alerts: { ...draft.alerts, enabled: v } })}
                label="Your materials: new stock and price moves"
                help={`New lots and price drops in the alloys you search, view and order. ${defaults.optIn ? "On" : "Off"} by default; only sent when there is something new.`}
              />
              {alertsOn && (
                <div className="mt-2 flex flex-wrap items-center gap-3 pl-6 text-sm">
                  <span className="text-gray-600">How often:</span>
                  {FREQUENCIES.map((f) => (
                    <label key={f.value} className="flex items-center gap-1">
                      <input
                        type="radio"
                        name="digest-frequency"
                        checked={frequency === f.value}
                        onChange={() => set({ alerts: { ...draft.alerts, frequency: f.value } })}
                      />
                      {f.label}
                      {f.value === defaults.frequency && draft.alerts.frequency == null ? " (default)" : ""}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          {available.quarterly && (
            <div className="rounded border border-gray-200 bg-white p-3">
              <Switch
                on={draft.quarterly.enabled ?? effective.quarterly}
                onChange={(v) => set({ quarterly: { ...draft.quarterly, enabled: v } })}
                label="Quarterly summary"
                help="Once a quarter: what you bought, what you saved and what is new in your materials."
              />
            </div>
          )}
          {available.deals && (
            <div className="rounded border border-gray-200 bg-white p-3">
              <Switch
                on={draft.deals.enabled ?? effective.deals}
                onChange={(v) => set({ deals: { enabled: v } })}
                label="Early access to deals"
                help="Promotions on materials you buy or search, shown to you a few days before everyone else (on the site and by email)."
              />
            </div>
          )}
        </div>
      )}

      {available.specs && (
        <div className="mt-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Saved specs profile</p>
          <p className="mt-1 text-xs text-gray-600">
            Your searches start with these filters applied; you can still remove them on any search.
          </p>
          <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded border border-gray-200 bg-white p-3">
              <p className="text-sm font-medium text-gray-800">Certs and compliance I always need</p>
              <div className="mt-2 space-y-1">
                {COMPLIANCE.map((c) => (
                  <label key={c.key} className="flex items-center gap-2 text-sm text-gray-800">
                    <input
                      type="checkbox"
                      checked={draft.specs.compliance.includes(c.key)}
                      onChange={(e) =>
                        setSpecs({
                          compliance: e.target.checked
                            ? [...draft.specs.compliance, c.key]
                            : draft.specs.compliance.filter((k) => k !== c.key),
                        })
                      }
                    />
                    {c.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="rounded border border-gray-200 bg-white p-3">
              <label className="block text-sm font-medium text-gray-800">
                Standards I work to
                <input
                  type="text"
                  className={`${input} mt-1 w-full font-normal`}
                  placeholder="AMS 5662, ASTM B637, AMS 4928"
                  value={standardsText}
                  onChange={(e) => {
                    setStandardsText(e.target.value);
                    setSaved(false);
                  }}
                />
                <span className="mt-1 block text-xs font-normal text-gray-600">
                  Comma-separated. When a search names a standard, only items that meet it are shown.
                </span>
              </label>
              <label className="mt-3 block text-sm font-medium text-gray-800">
                Tolerances and notes
                <textarea
                  className={`${input} mt-1 w-full font-normal`}
                  rows={2}
                  placeholder="e.g. +0.000 / -0.005 on bar diameter; cut lengths +1/8"
                  value={draft.specs.tolerancesNote}
                  onChange={(e) => setSpecs({ tolerancesNote: e.target.value.slice(0, 500) })}
                />
                <span className="mt-1 block text-xs font-normal text-gray-600">Goes with every RFQ you send us.</span>
              </label>
            </div>
            <div className="rounded border border-gray-200 bg-white p-3">
              <p className="text-sm font-medium text-gray-800">Preferred ship-from states</p>
              <p className="mt-0.5 text-xs text-gray-600">We favor stock in these states when quoting and matching your RFQs.</p>
              <div className="mt-2 flex flex-wrap gap-1">
                {draft.specs.preferredStates.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="rounded-full border border-gray-300 px-2 py-0.5 text-xs hover:bg-gray-100"
                    title="Remove"
                    onClick={() => setSpecs({ preferredStates: draft.specs.preferredStates.filter((x) => x !== s) })}
                  >
                    {s} x
                  </button>
                ))}
                <select
                  className={`${input} text-xs`}
                  value=""
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v && !draft.specs.preferredStates.includes(v)) setSpecs({ preferredStates: [...draft.specs.preferredStates, v] });
                  }}
                >
                  <option value="">Add a state...</option>
                  {US_STATES.filter((s) => !draft.specs.preferredStates.includes(s)).map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="rounded border border-gray-200 bg-white p-3">
              <label className="block text-sm font-medium text-gray-800">
                Longest lead time I can accept (days)
                <input
                  type="number"
                  min={1}
                  max={365}
                  className={`${input} mt-1 w-32 font-normal`}
                  value={draft.specs.maxLeadTimeDays ?? ""}
                  onChange={(e) => setSpecs({ maxLeadTimeDays: e.target.value === "" ? null : Math.max(1, Math.min(365, Number(e.target.value) || 1)) })}
                />
                <span className="mt-1 block text-xs font-normal text-gray-600">Blank = no limit. Used when we quote and match your RFQs.</span>
              </label>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          disabled={saving}
          onClick={save}
          className="rounded-full bg-[#00062e] px-6 py-2 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save preferences"}
        </button>
        {saved && <span className="text-sm text-green-800">Saved.</span>}
        {error && <span className="text-sm text-[#b91c1c]">{error}</span>}
      </div>
    </div>
  );
};
